import QueryParamsUtil from 'api/utils/query-params-util'
import axios from 'axios'

const API_PATH = 'api/v2/admins/merchants/inbounds'

const MerchantInboundApiV2 = {
  /**
   * Schedule particular `Merchant::Inbound`
   *
   * Invoke `POST api/v2/admins/merchant/inbounds/receive`
   * @param {object} merchant_inbound - Merchant Inbound
   * @param {number} merchant_inbound.inbound_id - Merchant Inbound ID
   * @param {Date} merchant_inbound.scheduled_at - Merchant Inbound Schedule Date
   * @param {string} merchant_inbound.courier Courier of the Merchant Inbound
   */
  schedule: async ({
    inbound_id,
    scheduled_at,
    courier
  }) =>  {
    const path = `${API_PATH}/${ inbound_id }/schedule`
    const body = {
      merchant_inbound: {
        scheduled_at : scheduled_at,
        courier      : courier
      }
    }

    const response = axios.patch(path, body)
    return response
  },
  /**
   * Create `Merchant::Inbound`
   * @param {object} params new `Merchant::Inbound`
   * @return {object} Object of `Merchant::Inbound`
   */
  create: async (params) => {
    const body     = {
      inbound: params
    }
    const path     = API_PATH
    const response = axios.post(path, body)
    return response
  },
  /**
   * Get list of `Merchant::Inbound`
   * @param {object} params - Filter Object
   * @param {object} params.tableState - ReactTable final state
   * @param {object} params.additionalFilters - A plain object to get customize filters outside `tableState`
   */
  get: async ({ tableState, additionalFilters }) => {
    const params = QueryParamsUtil.format(tableState, additionalFilters)
    const path   = API_PATH

    const response = axios.get(path, { params })
    return response
  },

  /**
   * Show Merchant Inbound by specific id
   * Inovke `GET api/v2/admins//merchant/inbounds/:id`
   * @param {number} id Merchant Inbound's ID
   */
  show: async (id) => {
    const path     = `${API_PATH}/${id}`
    const response = axios.get(path)
    return response
  },

  /**
   * Invoke `POST api/v2/admins/merchants/inbounds/:id/receive`
   * @param {number} inbound_id Seller Inbound ID
   * @param {number} tracking_no Tracking Number Inbound
   * @param {number} received_quantity Received Quantity of the inbound
   * @param {number} weight Weight of the inbound
   */
  receive: async ({
    id,
    tracking_no,
    received_quantity,
    weight
  }) =>  {
    const path     = `${API_PATH}/${ id }/receive`
    const body     = {
      merchant_inbound: {
        tracking_no       : tracking_no,
        received_quantity : received_quantity,
        weight            : weight
      }
    }
    const response = axios.patch(path, body)
    return response
  },
  /**
   * Cancel a `Merchant::Inbound`
   * @param {number} id - Merchant::Inbound.id to cancel
   */
  cancel: async (id) =>  {
    const path     = `${API_PATH}/${ id }/cancel`
    const response = axios.patch(path)
    return response
  },

  /**
   * Show Merchant Inbound Items by specific inbound_id
   * Inovke `GET api/v2/admins//merchant/inbounds/:id/items`
   * @param {number} id Merchant Inbound's ID
   */
  getItems: async ({ tableState, additionalFilters, inbound_id }) => {
    const params = QueryParamsUtil.format(tableState, additionalFilters)
    const path   = `${API_PATH}/${inbound_id}/items`

    const response = axios.get(path, { params })
    return response
  },

  /**
   * make live per merchant inbound
   * Inovke `PATCH api/v2/admins/merchant/inbounds/:id/make_live`
   * @param {number} id - Merchant Product Id
   */
  makeLive: async ({ id }) => {
    const endpoint = `${API_PATH}/${id}/make_live`
    const response = axios.patch(endpoint)
    return response
  },

  /**
   * Invoke `POST api/v2/admins/merchants/inbounds/items/:id/validate_import`
   * endpoint to validate rows value from Csv upload form, in import-csv page
   * @param {object} items - Merchant Inbound Items would be imported from csv file
   * @param {string} category - Merchant Product category to import
   */
  validate_import: async (
    id,
    items,
    category
  ) =>  {
    const path     = `${API_PATH}/${ id }/items/validate_import`
    const body     = {
      validate: {
        parent_category : category,
        items           : items
      }
    }
    const response = axios.post(path, body)
    return response
  },

  /**
   * Invoke `POST api/v2/admins/merchants/inbounds/items/:id/import`
   * endpoint to import rows value from Csv upload form, in import-csv page
   * @param {object} items - Merchant Inbound Items would be imported from csv file
   */
  import_csv: async (
    id,
    items,
    filename,
    merchantType,
    preOrderDays
  ) =>  {
    const path     = `${API_PATH}/${ id }/items/import`
    const body     = {
      items         : items,
      filename      : filename,
      merchant_type : merchantType,
      preorder_days : preOrderDays
    }
    const response = axios.post(path, body)
    return response
  },

  /**
   * Invoke `POST api/v2/admins/merchants/inbounds/items/:id/restock_import`
   * endpoint to restock import rows value from Csv upload form, in restock-import-csv page
   * @param {object} items - Merchant Inbound Items would be restock imported from csv file
   */
  restock_import_csv: async (
    id,
    items,
    filename,
    merchantType,
    preOrderDays
  ) =>  {
    const path     = `${API_PATH}/${ id }/items/restock_import`
    const body     = {
      items         : items,
      filename      : filename,
      merchant_type : merchantType,
      preorder_days : preOrderDays
    }
    const response = axios.post(path, body)
    return response
  },

  /**
   * Invoke `PATCH api/v2/admins/merchants/inbounds/curations/:id/photo_curated`
   * endpoint to make photo curation status completed for merchant inbound
   */
  completePhotoCuration: async ({
    curation_id
  }) =>  {
    const path     = `${API_PATH}/curations/${curation_id}/photo_curated`
    const response = axios.patch(path)
    return response
  },

  /**
   * reset inbound curation status to pending
   * Inovke `PATCH api/v2/admins/merchant/inbounds/:id/reset_import`
   * @param {number} id - Merchant Product Id
   */
  resetInbound: async ({ id }) => {
    const endpoint = `${API_PATH}/${id}/reset_import`
    const response = axios.patch(endpoint)
    return response
  }
}

export default MerchantInboundApiV2
